/* =========================================================================== *\
 Manual stock adjustment for developer / high-level users only
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import MDSnackbar from 'components/MDSnackbar';
import MDSelect from 'components/MDSelect';
import TableHeader from 'components/Table/TableHeader';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const mod = 'developer_stock';

const initFind = {
  search: '',
  branch_id: 'ALL',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  date_start: Func.dateTimeAdd(-30),
  date_stop: Func.dateTimeNow(),
};

const initForm = {
  branch_id: '',
  barcode_id: 0,
  barcode: '',
  product_name: '',
  unit_name: '',
  adjust_mode: 'delta',
  quantity: '',
  remark: '',
  current_stock: null,
};

const initProductFind = {
  search: '',
  order_by: 'pd.barcode',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
};

function DeveloperStock() {
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(initForm);
  const [branchList, setBranchList] = useState([]);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [productOpen, setProductOpen] = useState(false);
  const [productList, setProductList] = useState([]);
  const [productFind, setProductFind] = useState(initProductFind);
  const [productLoading, setProductLoading] = useState(false);
  const [productPageTotal, setProductPageTotal] = useState(1);
  const [productPageNo, setProductPageNo] = useState(1);

  const initHeader = [
    { id: 'tb.code', label: 'เลขที่', align: 'left' },
    { id: 'tb.created_at', label: t('attr.created_at'), align: 'center' },
    { id: 'branch', label: t('attr.branch'), align: 'left', sort: false },
    { id: 'product', label: t('menu.product'), align: 'left', sort: false },
    { id: 'tb.adjust_mode', label: 'รูปแบบ', align: 'center', sort: false },
    { id: 'tb.quantity_before', label: 'ก่อนปรับ', align: 'right' },
    { id: 'tb.quantity_diff', label: 'ปรับ', align: 'right', sort: false },
    { id: 'tb.quantity_after', label: 'หลังปรับ', align: 'right' },
    { id: 'user', label: t('menu.user'), align: 'left', sort: false },
    { id: 'remark', label: t('attr.remark'), align: 'left', sort: false },
  ];

  const productHeader = [
    { id: 'pd.barcode', label: 'Barcode', align: 'left' },
    { id: 'pd.product_name', label: t('menu.product'), align: 'left' },
    { id: 'pd.unit_id', label: t('menu.unit'), align: 'left' },
    { id: 'select', label: '', align: 'center', sort: false },
  ];

  const showNotify = (msg, color = 'error') => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t('menu.developer_stock')}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open
        close={() => setNotify(null)}
      />
    );
    setTimeout(() => setNotify(null), 4000);
  };

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {}).then((result) => {
      if (result.status === 'ok') {
        setBranchList(result.branch ?? []);
      } else {
        showNotify(result.msg || 'โหลดข้อมูลไม่สำเร็จ');
      }
    });
  };

  const loadCurrentStock = (branchId, barcodeId) => {
    if (!branchId || !barcodeId) {
      setForm((prev) => ({ ...prev, current_stock: null }));
      return;
    }

    FetchData('GET', `/${mod}?branch_id=${branchId}&barcode_id=${barcodeId}`, app.token, {}).then(
      (result) => {
        if (result.status === 'ok' && result.stock) {
          setForm((prev) => ({ ...prev, current_stock: result.stock.quantity }));
        }
      }
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      branch_id: data.branch_id,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data ?? []);
        setPageTotal(result.total_pages ?? 1);
        setPageNo(result.page ?? 1);
      } else {
        showNotify(result.msg || 'โหลดประวัติไม่สำเร็จ');
      }
      setLoading(false);
    });
  };

  const searchProduct = (data) => {
    setProductFind(data);
    setProductLoading(true);
    setProductList([]);

    FetchData('OPTIONS', '/product/search', app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    })
      .then((result) => {
        if (result.status === 'ok') {
          setProductList(result.data ?? []);
          setProductPageTotal(result.total_pages ?? 1);
          setProductPageNo(result.page ?? 1);
        }
        setProductLoading(false);
      })
      .catch(() => setProductLoading(false));
  };

  const selectProduct = (product) => {
    const barcodeId = product.id ?? product.barcode_id;
    setForm((prev) => ({
      ...prev,
      barcode_id: barcodeId,
      barcode: product.barcode,
      product_name: product.product_name ?? product.name,
      unit_name: product.unit_name,
    }));
    setProductOpen(false);
    loadCurrentStock(form.branch_id, barcodeId);
  };

  const handleBranchChange = (branchId) => {
    setForm((prev) => ({ ...prev, branch_id: branchId, current_stock: null }));
    if (branchId && form.barcode_id) {
      loadCurrentStock(branchId, form.barcode_id);
    }
  };

  const resetForm = () => {
    setForm(initForm);
  };

  const submitAdjust = () => {
    if (!form.branch_id) {
      showNotify('กรุณาเลือกสาขา', 'warning');
      return;
    }
    if (!form.barcode_id) {
      showNotify('กรุณาเลือกสินค้า', 'warning');
      return;
    }
    if (form.quantity === '' || Number.isNaN(Number(form.quantity))) {
      showNotify('กรุณาระบุจำนวน', 'warning');
      return;
    }

    const modeLabel =
      form.adjust_mode === 'set' ? 'ตั้งเป็นจำนวนที่ระบุ' : 'เพิ่ม/ลดตามจำนวนที่ระบุ';
    const currentText =
      form.current_stock != null ? Func.numberDisplay(form.current_stock, 2) : '-';

    Swal.fire({
      title: 'ยืนยันการปรับ Stock?',
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.8">
          <div><b>สาขา:</b> ${
            branchList.find((b) => `${b.id}` === `${form.branch_id}`)?.name || form.branch_id
          }</div>
          <div><b>สินค้า:</b> ${form.barcode} - ${form.product_name}</div>
          <div><b>Stock ปัจจุบัน:</b> ${currentText}</div>
          <div><b>รูปแบบ:</b> ${modeLabel}</div>
          <div><b>จำนวน:</b> ${form.quantity}</div>
          <div><b>หมายเหตุ:</b> ${form.remark || '-'}</div>
        </div>
      `,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: 'ยืนยัน',
      denyButtonText: 'ยกเลิก',
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (!swal.isConfirmed) return;

      setSubmitting(true);
      FetchData('PUT', `/${mod}`, app.token, {
        branch_id: form.branch_id,
        barcode_id: form.barcode_id,
        adjust_mode: form.adjust_mode,
        quantity: Number(form.quantity),
        remark: form.remark,
      }).then((result) => {
        setSubmitting(false);
        if (result.status === 'ok') {
          showNotify(
            result.msg === 'no_change'
              ? 'ไม่มีการเปลี่ยนแปลง Stock'
              : `ปรับ Stock สำเร็จ (${Func.numberDisplay(
                  result.quantity_before,
                  2
                )} → ${Func.numberDisplay(result.quantity_after, 2)})`,
            'success'
          );
          resetForm();
          view({ ...find, page: 1 });
        } else {
          showNotify(result.msg || 'ปรับ Stock ไม่สำเร็จ');
        }
      });
    });
  };

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderMode = (mode) =>
    mode === 'set' ? (
      <Chip label='ตั้งค่า' color='info' size='small' />
    ) : (
      <Chip label='เพิ่ม/ลด' color='warning' size='small' />
    );

  const renderDiff = (value) => {
    const num = Number(value);
    if (num > 0) return <span style={{ color: '#2e7d32' }}>+{Func.numberDisplay(num, 2)}</span>;
    if (num < 0) return <span style={{ color: '#d32f2f' }}>{Func.numberDisplay(num, 2)}</span>;
    return Func.numberDisplay(num, 2);
  };

  const renderLoading = () => (
    <Backdrop
      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={loading || submitting}
    >
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t('element.show_10_record')}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t('element.show_20_record')}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t('element.show_100_record')}
      </MenuItem>
    </Menu>
  );

  const renderProductDialog = () => (
    <Dialog open={productOpen} onClose={() => setProductOpen(false)} maxWidth='md' fullWidth>
      <DialogTitle>เลือกสินค้า</DialogTitle>
      <DialogContent dividers>
        <MDBox pb={2}>
          <MDInput
            fullWidth
            placeholder={t('element.search')}
            value={productFind.search}
            onChange={(e) => setProductFind({ ...productFind, search: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && searchProduct({ ...productFind, page: 1 })}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Icon>search</Icon>
                </InputAdornment>
              ),
            }}
          />
        </MDBox>
        <MDButton
          variant='gradient'
          color='info'
          onClick={() => searchProduct({ ...productFind, page: 1 })}
          sx={{ mb: 2 }}
        >
          <Icon>search</Icon>&nbsp;ค้นหา
        </MDButton>
        <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
          <Table size='small'>
            <TableHeader data={productHeader} sort={false} />
            <TableBody>
              {productList.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.barcode}</TableCell>
                  <TableCell>{row.product_name ?? row.name}</TableCell>
                  <TableCell>{row.unit_name}</TableCell>
                  <TableCell align='center'>
                    <MDButton
                      variant='outlined'
                      color='info'
                      size='small'
                      onClick={() => selectProduct(row)}
                    >
                      เลือก
                    </MDButton>
                  </TableCell>
                </TableRow>
              ))}
              {productList.length === 0 && !productLoading && (
                <TableRow>
                  <TableCell colSpan={4} align='center'>
                    ไม่พบสินค้า
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <MDBox display='flex' justifyContent='center' mt={2}>
          <Pagination
            count={productPageTotal}
            variant='outlined'
            color='secondary'
            page={productPageNo}
            onChange={(e, p) => searchProduct({ ...productFind, page: p })}
          />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton color='secondary' onClick={() => setProductOpen(false)}>
          ปิด
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
    view(initFind);
  }, []);

  useEffect(() => {
    if (productOpen) {
      searchProduct(initProductFind);
    }
  }, [productOpen]);

  return (
    <DashboardLayout>
      <Navbar route={[t('menu.setting'), t('menu.developer_stock')]} />
      <MDBox pt={2} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant='h6' fontWeight='medium' mb={2}>
                  ปรับ Stock แบบ Manual (Developer)
                </MDTypography>
                <MDTypography variant='button' color='text' display='block' mb={2}>
                  ใช้สำหรับแก้ไข Stock โดยตรง entity = developer_stocks — กรุณาใช้ด้วยความระมัดระวัง
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <MDSelect
                      label={t('attr.branch')}
                      labelDefault={t('element.pleaseselect')}
                      valueDefault=''
                      data={branchList}
                      value={form.branch_id}
                      onChange={(e) => handleBranchChange(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <MDInput
                      fullWidth
                      label={t('menu.product')}
                      value={
                        form.barcode_id
                          ? `${form.barcode} - ${form.product_name} (${form.unit_name})`
                          : ''
                      }
                      placeholder='คลิกเพื่อเลือกสินค้า'
                      onClick={() => setProductOpen(true)}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position='end'>
                            <Icon sx={{ cursor: 'pointer' }} onClick={() => setProductOpen(true)}>
                              search
                            </Icon>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      fullWidth
                      label='Stock ปัจจุบัน (สาขาที่เลือก)'
                      value={
                        form.current_stock != null ? Func.numberDisplay(form.current_stock, 2) : '-'
                      }
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <MDTypography variant='caption' color='text' display='block' mb={1}>
                      รูปแบบการปรับ
                    </MDTypography>
                    <RadioGroup
                      row
                      value={form.adjust_mode}
                      onChange={(e) => setForm({ ...form, adjust_mode: e.target.value })}
                    >
                      <FormControlLabel
                        value='set'
                        control={<Radio color='info' />}
                        label='ตั้งเป็นจำนวนที่ระบุ'
                      />
                      <FormControlLabel
                        value='delta'
                        control={<Radio color='warning' />}
                        label='เพิ่ม/ลดตามจำนวนที่ระบุ (+ เพิ่ม, - ลด)'
                      />
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      fullWidth
                      type='number'
                      label={form.adjust_mode === 'set' ? 'จำนวนที่ต้องการ' : 'จำนวนที่ปรับ (+/-)'}
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <MDInput
                      fullWidth
                      label={t('attr.remark')}
                      value={form.remark}
                      onChange={(e) => setForm({ ...form, remark: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox display='flex' gap={1}>
                      <MDButton variant='gradient' color='error' onClick={submitAdjust}>
                        <Icon>inventory_2</Icon>&nbsp;บันทึกการปรับ Stock
                      </MDButton>
                      <MDButton variant='outlined' color='secondary' onClick={resetForm}>
                        ล้างฟอร์ม
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={2}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      fullWidth
                      value={find.search}
                      placeholder={t('element.search')}
                      onChange={(e) => setFind({ ...find, search: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && view({ ...find, page: 1 })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <Icon>search</Icon>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDSelect
                      label={t('attr.branch')}
                      labelDefault='ทั้งหมด'
                      valueDefault='ALL'
                      data={branchList}
                      value={find.branch_id}
                      onChange={(e) => view({ ...find, branch_id: e.target.value, page: 1 })}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <MDButton
                      variant='gradient'
                      color='info'
                      fullWidth
                      onClick={() => view({ ...find, page: 1 })}
                    >
                      <Icon>search</Icon>&nbsp;ค้นหา
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
                <Table>
                  <TableHeader
                    data={initHeader}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleSort}
                  />
                  <TableBody>
                    {record.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.code}</TableCell>
                        <TableCell align='center'>{Func.dateTimeDisplay(row.created_at)}</TableCell>
                        <TableCell>{row.branch_name}</TableCell>
                        <TableCell>
                          {row.barcode} - {row.product_name}
                        </TableCell>
                        <TableCell align='center'>{renderMode(row.adjust_mode)}</TableCell>
                        <TableCell align='right'>
                          {Func.numberDisplay(row.quantity_before, 2)}
                        </TableCell>
                        <TableCell align='right'>{renderDiff(row.quantity_diff)}</TableCell>
                        <TableCell align='right'>
                          {Func.numberDisplay(row.quantity_after, 2)}
                        </TableCell>
                        <TableCell>{row.user_name}</TableCell>
                        <TableCell>{row.remark || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {record.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={10} align='center'>
                          ไม่พบข้อมูล
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t('element.show_record', { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      {renderProductDialog()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default DeveloperStock;
